import { useState, useCallback, useMemo, memo } from "react";
import { FixedSizeList } from "react-window";

const API_BASE = process.env.REACT_APP_API_BASE || "https://your-api-gateway.execute-api.us-east-1.amazonaws.com/prod";

// ── Sources — mirrors Power Apps colSources ───────────────────────────────
const SOURCES = [
  { id: "048025f3758f444bbb243f903b863d7a", name: "myHR Talent Solutions 2.0 - Avature(Production)" },
  { id: "86f7c6be24a0440fa8fb81de21ef1520", name: "myHR Visier PROD" },
  { id: "2c91808481de960c0181df3d2caf0f91", name: "myHR InforIFS PROD" },
  { id: "2c91808881de99980181df2e07c41030", name: "myHR Tableau PROD" },
  { id: "6b4889aa246b47fbb9f3ba88819eea90", name: "myHR SAP P41 BE" },
  { id: "2c918086825f987f018263b9882c3d41", name: "myHR InforLandmark PROD" },
  { id: "2c91808881eee3820181f7b061445982", name: "myHR ServiceNow PROD" },
  { id: "d2d8fa01491c453e97dc854059a61505", name: "QuickSight" },
];

function csvEscape(value) {
  const str = String(value ?? "");
  return `"${str.replace(/"/g, '""')}"`;
}

function downloadIdentityCsv(rows, sourceName) {
  const generatedAt = new Date().toISOString();
  const columns = [
    "Source",
    "Access Profile Name",
    "Employee Number",
    "Display Name",
    "Position",
    "Email",
  ];

  const csvRows = rows.map((row) => [
    sourceName,
    row.accessProfileName,
    row.employeeNumber,
    row.displayName,
    row.position,
    row.email,
  ].map(csvEscape).join(","));

  const csv = [columns.map(csvEscape).join(","), ...csvRows].join("\n");

  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `entitlement-identity-export-${Date.now()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Memoized Profile Row component ───────────────────────────────────
const ProfileRow = memo(({ profile, isSelected, onToggle }) => (
  <div
    className={`el-profile-row ${isSelected ? "el-profile-row-selected" : ""}`}
    onClick={() => onToggle(profile)}
  >
    <input
      type="checkbox"
      checked={isSelected}
      onChange={() => onToggle(profile)}
      onClick={(e) => e.stopPropagation()}
      aria-label={`Select ${profile.AccessProfileName}`}
    />
    <div className="el-profile-indicator" />
    <div className="el-profile-content">
      <div className="el-profile-name">{profile.AccessProfileName}</div>
      {profile.Description && <div className="el-profile-desc">{profile.Description}</div>}
    </div>
    {profile.EntitlementCount > 0 && (
      <span className="el-entitlement-badge">{profile.EntitlementCount}</span>
    )}
  </div>
));
ProfileRow.displayName = 'ProfileRow';

// ── Virtualized row for react-window ──────────────────────────────────
const VirtualRow = memo(({ index, style, data }) => {
  const { filtered, selectedSet, onToggleSelect } = data;
  const profile = filtered[index];
  
  return (
    <div style={style}>
      <ProfileRow
        profile={profile}
        isSelected={selectedSet.has(profile.AccessProfileID)}
        onToggle={onToggleSelect}
      />
    </div>
  );
});
VirtualRow.displayName = 'VirtualRow';

// ── Access Profile list panel ─────────────────────────────────────────
function ProfileList({ profiles, loading, selectedIds, onToggleSelect, searchText, onSearchChange }) {
  // Memoize filtered list to avoid recalculating on every render
  const filtered = useMemo(() => 
    profiles.filter(p =>
      !searchText ||
      p.AccessProfileName?.toLowerCase().includes(searchText.toLowerCase()) ||
      p.Description?.toLowerCase().includes(searchText.toLowerCase()) ||
      p.AccessProfileID?.toLowerCase().includes(searchText.toLowerCase())
    ),
    [profiles, searchText]
  );

  // Convert selectedIds array to Set for O(1) lookup
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  
  // Memoize item data to prevent unnecessary re-renders
  const itemData = useMemo(() => ({
    filtered,
    selectedSet,
    onToggleSelect
  }), [filtered, selectedSet, onToggleSelect]);

  return (
    <div className="el-panel el-profiles-panel">
      <div className="el-panel-header">
        <div className="el-panel-title">Access Profiles</div>
        <div className="el-panel-count">{selectedIds.length} selected</div>
      </div>

      {profiles.length > 0 && (
        <div className="el-search-wrap">
          <svg className="el-search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            className="el-search-input"
            placeholder="Search access profiles…"
            value={searchText}
            onChange={e => onSearchChange(e.target.value)}
          />
          {searchText && (
            <button className="el-search-clear" onClick={() => onSearchChange("")}>✕</button>
          )}
        </div>
      )}

      <div className="el-profile-list">
        {loading && (
          <div className="el-list-state">
            <span className="spinner el-spinner" />
            Loading access profiles…
          </div>
        )}

        {!loading && profiles.length === 0 && (
          <div className="el-list-state el-list-empty">
            Select a source and click<br /><strong>Load Access Profiles</strong>
          </div>
        )}

        {!loading && profiles.length > 0 && filtered.length === 0 && (
          <div className="el-list-state el-list-empty">No profiles match your search.</div>
        )}

        {!loading && filtered.length > 0 && (
          <FixedSizeList
            height={600}
            itemCount={filtered.length}
            itemSize={60}
            width="100%"
            itemData={itemData}
          >
            {VirtualRow}
          </FixedSizeList>
        )}
      </div>
    </div>
  );
}

// ── Identities table panel ────────────────────────────────────────────────
function IdentitiesPanel({ identities, loading, totalCount, selectedProfiles }) {
  const [searchText, setSearchText] = useState("");

  const filtered = identities.filter(id =>
    !searchText ||
    id.displayName?.toLowerCase().includes(searchText.toLowerCase()) ||
    id.email?.toLowerCase().includes(searchText.toLowerCase()) ||
    id.employeeNumber?.toLowerCase().includes(searchText.toLowerCase()) ||
    id.position?.toLowerCase().includes(searchText.toLowerCase())
  );

  const sorted = [...filtered].sort((a, b) => (a.displayName || "").localeCompare(b.displayName || ""));

  return (
    <div className="el-panel el-identities-panel">
      <div className="el-panel-header">
        <div>
          <div className="el-panel-title">Assigned Identities</div>
          {selectedProfiles.length > 0 && (
            <div className="el-panel-subtitle">
              {selectedProfiles.length} access profile{selectedProfiles.length !== 1 ? "s" : ""} selected
            </div>
          )}
        </div>
        {identities.length > 0 && (
          <div className="el-panel-count">
            {identities.length}{totalCount > identities.length ? ` of ${totalCount}` : ""} loaded
          </div>
        )}
      </div>

      {identities.length > 0 && (
        <div className="el-search-wrap">
          <svg className="el-search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            className="el-search-input"
            placeholder="Search by name, position, email, employee #…"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
          {searchText && (
            <button className="el-search-clear" onClick={() => setSearchText("")}>✕</button>
          )}
        </div>
      )}

      <div className="el-identities-body">
        {loading && (
          <div className="el-list-state">
            <span className="spinner el-spinner" />
            Loading identities…
          </div>
        )}

        {!loading && selectedProfiles.length === 0 && (
          <div className="el-list-state el-list-empty">
            Select one or more access profiles and click<br /><strong>Load Identities</strong>
          </div>
        )}

        {!loading && selectedProfiles.length > 0 && identities.length === 0 && (
          <div className="el-list-state el-list-empty">No identities found for the selected profiles.</div>
        )}

        {!loading && identities.length > 0 && (
          <>
            <div className="el-table-wrap">
              <div className="el-thead">
                <div className="el-th" style={{ width: 260 }}>Access Profile</div>
                <div className="el-th" style={{ width: 120 }}>Employee #</div>
                <div className="el-th" style={{ flex: 1 }}>Display Name</div>
                <div className="el-th" style={{ flex: 1.2 }}>Position</div>
                <div className="el-th" style={{ flex: 1.6 }}>Email</div>
              </div>
              <div className="el-tbody">
                {sorted.length === 0 ? (
                  <div className="el-table-empty">No identities match your search.</div>
                ) : sorted.map((id, i) => (
                  <div key={id.id || i} className="el-tr">
                    <div className="el-td" style={{ width: 260 }}>
                      <span className="el-display-name">{id.accessProfileName || "-"}</span>
                    </div>
                    <div className="el-td" style={{ width: 120 }}>
                      {id.employeeNumber
                        ? <span className="el-emp-badge">{id.employeeNumber}</span>
                        : <span className="el-td-muted">—</span>}
                    </div>
                    <div className="el-td" style={{ flex: 1 }}>
                      <span className="el-display-name">{id.displayName || "—"}</span>
                    </div>
                    <div className="el-td" style={{ flex: 1.2 }}>{id.position || "—"}</div>
                    <div className="el-td el-td-email" style={{ flex: 1.6 }}>{id.email || "—"}</div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────
export default function EntitlementLookup() {
  const [selectedSourceId,  setSelectedSourceId]  = useState(SOURCES[0].id);
  const [profiles,          setProfiles]          = useState([]);
  const [loadingProfiles,   setLoadingProfiles]   = useState(false);
  const [profileSearch,     setProfileSearch]     = useState("");
  const [selectedProfileIds,setSelectedProfileIds]= useState([]);

  const [identities,        setIdentities]        = useState([]);
  const [loadingIdentities, setLoadingIdentities] = useState(false);
  const [totalCount,        setTotalCount]        = useState(0);
  const [profileError,      setProfileError]      = useState("");
  const [identityError,     setIdentityError]     = useState("");

  // ── Load access profiles for selected source ──────────────────────────
  const handleLoadProfiles = useCallback(async () => {
    if (!selectedSourceId) return;
    setLoadingProfiles(true);
    setProfileError("");
    setProfiles([]);
    setSelectedProfileIds([]);
    setIdentities([]);
    setProfileSearch("");
    
    try {
      // First batch: fetch up to 7,000 records (stays under 29s timeout)
      const res1 = await fetch(`${API_BASE}/entitlements-by-source`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          sourceId: selectedSourceId,
          offset: 0,
          limit: 7000
        }),
      });
      const data1 = await res1.json();
      if (!res1.ok) throw new Error(data1?.error || `API error ${res1.status}`);
      
      const firstBatch = data1.accessProfiles || [];
      setProfiles(firstBatch);
      
      // If there's more data, automatically fetch the rest
      if (data1.hasMore) {
        const totalCount = data1.totalCount || 0;
        const remaining = totalCount - data1.currentOffset;
        console.log(`Auto-loading remaining ${remaining} of ${totalCount} profiles...`);
        
        // Update UI to show we're loading more (profiles are already shown)
        // The loading indicator stays on to show we're still fetching
        
        try {
          const res2 = await fetch(`${API_BASE}/entitlements-by-source`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ 
              sourceId: selectedSourceId,
              offset: data1.currentOffset,
              limit: remaining
            }),
          });
          const data2 = await res2.json();
          if (!res2.ok) throw new Error(data2?.error || `API error ${res2.status}`);
          
          const secondBatch = data2.accessProfiles || [];
          // Append second batch to first batch
          setProfiles([...firstBatch, ...secondBatch]);
          console.log(`Loaded all ${totalCount} profiles`);
        } catch (err) {
          // If second batch fails, we still have the first 7,000
          console.error("Failed to load remaining profiles:", err.message);
          setProfileError(`Loaded ${firstBatch.length} of ${totalCount} profiles. ${err.message}`);
        }
      }
    } catch (err) {
      setProfileError(err.message || "Failed to load access profiles.");
    } finally {
      setLoadingProfiles(false);
    }
  }, [selectedSourceId]);

  // ── Helper to load identities for specific profile IDs ────────────────
  const handleLoadIdentitiesForIds = useCallback(async (profileIds) => {
    const profilesToLoad = profiles.filter(p => profileIds.includes(p.AccessProfileID));
    if (profilesToLoad.length === 0) return;
    
    setLoadingIdentities(true);
    setIdentityError("");
    setIdentities([]);
    setTotalCount(0);
    try {
      const requests = profilesToLoad.map(async (profile) => {
        const res = await fetch(`${API_BASE}/access-profile-identities`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ accessProfileId: profile.AccessProfileID }),
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(`${profile.AccessProfileName}: ${data?.error || `API error ${res.status}`}`);
        }
        return {
          profile,
          identities: data.identities || [],
        };
      });

      const results = await Promise.all(requests);
      const allIdentities = results.flatMap(r =>
        r.identities.map(ident => ({
          ...ident,
          accessProfileName: r.profile.AccessProfileName,
          accessProfileId: r.profile.AccessProfileID,
        }))
      );

      setIdentities(allIdentities);
      setTotalCount(allIdentities.length);
    } catch (err) {
      setIdentityError(err.message);
    } finally {
      setLoadingIdentities(false);
    }
  }, [profiles]);

  // ── Select a profile ──────────────────────────────────────────────────
  const handleToggleProfile = useCallback((profile) => {
    setSelectedProfileIds((prev) => {
      const wasSelected = prev.includes(profile.AccessProfileID);
      const newIds = wasSelected
        ? prev.filter((id) => id !== profile.AccessProfileID)
        : [...prev, profile.AccessProfileID];
      return newIds;
    });
    
    // Clear identities when selection changes (user needs to reload)
    setIdentities([]);
    setTotalCount(0);
    setIdentityError("");
  }, []);

  const selectedProfiles = useMemo(() => 
    profiles.filter((p) => selectedProfileIds.includes(p.AccessProfileID)),
    [profiles, selectedProfileIds]
  );
  const selectedSource = SOURCES.find((s) => s.id === selectedSourceId);

  // ── Load identities for selected profile ──────────────────────────────
  const handleLoadIdentities = useCallback(async () => {
    if (selectedProfileIds.length === 0) return;
    await handleLoadIdentitiesForIds(selectedProfileIds);
  }, [selectedProfileIds, handleLoadIdentitiesForIds]);

  const handleExportCsv = () => {
    if (identities.length === 0) return;
    downloadIdentityCsv(identities, selectedSource?.name || "Unknown Source");
  };

  // ── Source change ─────────────────────────────────────────────────────
  const handleSourceChange = (id) => {
    setSelectedSourceId(id);
    setProfiles([]);
    setSelectedProfileIds([]);
    setIdentities([]);
    setTotalCount(0);
    setProfileSearch("");
    setProfileError("");
    setIdentityError("");
  };

  return (
    <div className="el-root">

      {/* ── Header ── */}
      <div className="el-header">
        <div className="el-header-inner">
          <div className="logo-mark">V</div>
          <div>
            <div className="el-title">Entitlement to Identity Lookup</div>
            <div className="el-subtitle">Search access profiles by source, then find identities assigned to the selected profile</div>
          </div>
        </div>
      </div>

      {/* ── Toolbar ── */}
      <div className="el-toolbar">
        <div className="el-toolbar-inner">
          <div className="el-field">
            <label className="control-label">Source Application</label>
            <select
              className="el-source-select"
              value={selectedSourceId}
              onChange={e => handleSourceChange(e.target.value)}>
              {SOURCES.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          <button
            className="compare-btn"
            onClick={handleLoadProfiles}
            disabled={loadingProfiles || !selectedSourceId}>
            {loadingProfiles
              ? <><span className="spinner" /> Loading…</>
              : <>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.51"/>
                  </svg>
                  Load Access Profiles
                </>}
          </button>

          {selectedProfiles.length > 0 && (
            <>
              <div className="el-selected-chip">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                {selectedProfiles.length} selected
              </div>

              <button
                className="compare-btn"
                onClick={handleLoadIdentities}
                disabled={loadingIdentities || selectedProfiles.length === 0}>
                {loadingIdentities
                  ? <><span className="spinner" /> Loading…</>
                  : <>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                        <circle cx="9" cy="7" r="4"/>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                      </svg>
                      Load Identities
                    </>}
              </button>

              <button
                className="compare-btn"
                onClick={handleExportCsv}
                disabled={identities.length === 0}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Export CSV
              </button>
            </>
          )}
        </div>

        {/* Errors */}
        {profileError && (
          <div className="as-error-bar" style={{ marginTop: 12 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            {profileError}
          </div>
        )}
        {identityError && (
          <div className="as-error-bar" style={{ marginTop: 12 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            {identityError}
          </div>
        )}
      </div>

      {/* ── Selected Profiles Chips ── */}
      {selectedProfiles.length > 0 && (
        <div className="el-selected-profiles">
          <div className="el-selected-profiles-inner">
            <span className="el-selected-label">Selected Access Profiles:</span>
            <div className="el-selected-chips">
              {selectedProfiles.map(prof => (
                <div key={prof.AccessProfileID} className="el-profile-chip">
                  <span className="el-profile-chip-name">{prof.AccessProfileName}</span>
                  <button
                    className="el-profile-chip-remove"
                    onClick={() => handleToggleProfile(prof)}
                    title="Remove from selection">
                    ✕
                  </button>
                </div>
              ))}
            </div>
            {identities.length > 0 && (
              <button
                className="el-clear-all-btn"
                onClick={() => {
                  setSelectedProfileIds([]);
                  setIdentities([]);
                  setTotalCount(0);
                }}
                title="Clear all selections">
                Clear All
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Two-panel layout ── */}
      <div className="el-body">
        <ProfileList
          profiles={profiles}
          loading={loadingProfiles}
          selectedIds={selectedProfileIds}
          onToggleSelect={handleToggleProfile}
          searchText={profileSearch}
          onSearchChange={setProfileSearch}
        />
        <IdentitiesPanel
          identities={identities}
          loading={loadingIdentities}
          totalCount={totalCount}
          selectedProfiles={selectedProfiles}
        />
      </div>

      <style>{`
        /* ── Root ── */
        .el-root { min-height: 100%; background: linear-gradient(180deg, rgba(112, 204, 152, 0.04) 0, transparent 120px), var(--bg-base); display: flex; flex-direction: column; }

        /* ── Header ── */
        .el-header { background: rgba(35, 55, 71, 0.88); backdrop-filter: blur(10px); border-bottom: 1px solid var(--border); padding: 22px 32px; flex-shrink: 0; }
        .el-header-inner { display: flex; align-items: center; gap: 14px; }
        .el-title { font-family: var(--font-display); font-size: 18px; font-weight: 700; color: var(--text-primary); }
        .el-subtitle { font-size: 11px; color: var(--text-muted); margin-top: 2px; }

        /* ── Toolbar ── */
        .el-toolbar { background: rgba(35, 55, 71, 0.92); backdrop-filter: blur(10px); border-bottom: 1px solid var(--border); padding: 18px 32px; flex-shrink: 0; position: sticky; top: 0; z-index: 10; }
        .el-toolbar-inner { display: flex; align-items: flex-end; gap: 14px; flex-wrap: wrap; }
        .el-field { display: flex; flex-direction: column; gap: 6px; }
        .el-source-select { font-family: var(--font-body); font-size: 13px; background: var(--bg-raised); color: var(--text-primary); border: 1px solid var(--border-light); border-radius: var(--radius-md); padding: 8px 14px; min-width: 300px; cursor: pointer; outline: none; transition: border-color 0.15s; appearance: auto; }
        .el-source-select:focus { border-color: var(--green); }
        .el-selected-chip { display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--green-bright); background: rgba(112, 204, 152, 0.12); border: 1px solid rgba(112, 204, 152, 0.28); border-radius: var(--radius-md); padding: 7px 12px; max-width: 320px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; align-self: flex-end; height: 38px; }

        /* ── Selected Profiles Chips ── */
        .el-selected-profiles { background: rgba(35, 55, 71, 0.48); border-bottom: 1px solid var(--border); padding: 12px 32px; flex-shrink: 0; }
        .el-selected-profiles-inner { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
        .el-selected-label { font-size: 12px; color: var(--text-muted); font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em; white-space: nowrap; }
        .el-selected-chips { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; flex: 1; }
        .el-profile-chip { display: flex; align-items: center; gap: 8px; background: rgba(112, 204, 152, 0.14); border: 1px solid rgba(112, 204, 152, 0.32); border-radius: 6px; padding: 6px 10px; font-size: 12px; color: var(--green-bright); max-width: 280px; transition: all 0.15s; }
        .el-profile-chip:hover { background: rgba(112, 204, 152, 0.18); border-color: rgba(112, 204, 152, 0.45); }
        .el-profile-chip-name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-weight: 500; }
        .el-profile-chip-remove { background: transparent; border: none; color: var(--green-bright); font-size: 13px; cursor: pointer; padding: 0 2px; line-height: 1; display: flex; align-items: center; justify-content: center; transition: color 0.15s; flex-shrink: 0; }
        .el-profile-chip-remove:hover { color: var(--text-primary); }
        .el-clear-all-btn { background: transparent; border: 1px solid var(--border-light); border-radius: 6px; padding: 6px 12px; font-size: 12px; color: var(--text-muted); cursor: pointer; transition: all 0.15s; font-family: inherit; white-space: nowrap; }
        .el-clear-all-btn:hover { background: rgba(248, 81, 73, 0.08); border-color: rgba(248, 81, 73, 0.4); color: #ff9991; }

        /* ── Two-panel body ── */
        .el-body { display: grid; grid-template-columns: 340px 1fr; gap: 0; flex: 1; min-height: 0; overflow: hidden; }

        /* ── Panel shared ── */
        .el-panel { display: flex; flex-direction: column; border-right: 1px solid var(--border); overflow: hidden; background: rgba(35, 55, 71, 0.58); }
        .el-identities-panel { border-right: none; }
        .el-panel-header { padding: 18px 20px 14px; border-bottom: 1px solid var(--border); flex-shrink: 0; display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
        .el-panel-title { font-family: var(--font-display); font-size: 15px; font-weight: 700; color: var(--text-primary); }
        .el-panel-subtitle { font-size: 11px; color: var(--text-muted); margin-top: 3px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 400px; }
        .el-panel-count { font-size: 11px; color: var(--text-secondary); background: rgba(255, 255, 255, 0.03); border: 1px solid var(--border-light); border-radius: 999px; padding: 2px 10px; white-space: nowrap; flex-shrink: 0; }

        /* ── Search bar ── */
        .el-search-wrap { display: flex; align-items: center; gap: 8px; padding: 12px 16px; border-bottom: 1px solid var(--border); flex-shrink: 0; background: rgba(255, 255, 255, 0.02); }
        .el-search-icon { color: var(--text-muted); flex-shrink: 0; }
        .el-search-input { flex: 1; background: none; border: none; outline: none; font-family: var(--font-body); font-size: 13px; color: var(--text-primary); }
        .el-search-input::placeholder { color: var(--text-muted); }
        .el-search-clear { background: none; border: none; color: var(--text-muted); cursor: pointer; font-size: 12px; padding: 2px 4px; line-height: 1; transition: color 0.15s; }
        .el-search-clear:hover { color: var(--red); }

        /* ── Profile list ── */
        .el-profile-list { flex: 1; overflow-y: auto; }
        .el-list-state { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; padding: 48px 24px; font-size: 13px; color: var(--text-muted); text-align: center; line-height: 1.6; }
        .el-list-state strong { color: var(--text-secondary); }
        .el-list-empty { font-style: italic; }
        .el-spinner { border-color: var(--border) !important; border-top-color: var(--green) !important; }

        .el-profile-row { display: flex; align-items: center; gap: 10px; padding: 11px 16px; border-bottom: 1px solid var(--border); cursor: pointer; transition: background 0.1s; }
        .el-profile-row:last-child { border-bottom: none; }
        .el-profile-row:hover { background: var(--bg-hover); }
        .el-profile-row-selected { background: rgba(112, 204, 152, 0.12) !important; }
        .el-profile-row-selected .el-profile-indicator { background: var(--green); }
        .el-profile-row-selected .el-profile-name { color: var(--green-bright); }

        .el-profile-indicator { width: 3px; height: 36px; border-radius: 2px; background: transparent; flex-shrink: 0; transition: background 0.15s; }
        .el-profile-row:hover .el-profile-indicator { background: var(--border-light); }

        .el-profile-content { flex: 1; min-width: 0; }
        .el-profile-name { font-size: 12px; font-weight: 500; color: var(--text-primary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .el-profile-desc { font-size: 11px; color: var(--text-muted); margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .el-entitlement-badge { font-size: 10px; padding: 2px 7px; border-radius: 999px; background: rgba(0, 155, 221, 0.12); color: var(--sp-green-pale); border: 1px solid rgba(0, 155, 221, 0.3); white-space: nowrap; flex-shrink: 0; }

        /* ── Identities panel ── */
        .el-identities-body { flex: 1; overflow: hidden; display: flex; flex-direction: column; }

        /* ── Identities table ── */
        .el-table-wrap { flex: 1; overflow: auto; }
        .el-thead { display: flex; background: rgba(255, 255, 255, 0.03); border-bottom: 1px solid var(--border); position: sticky; top: 0; z-index: 2; }
        .el-th { font-size: 10px; text-transform: uppercase; letter-spacing: 0.07em; color: var(--text-muted); padding: 9px 14px; flex-shrink: 0; }
        .el-th:not(:last-child) { border-right: 1px solid var(--border); }
        .el-tbody { display: flex; flex-direction: column; }
        .el-tr { display: flex; border-bottom: 1px solid var(--border); transition: background 0.1s; }
        .el-tr:last-child { border-bottom: none; }
        .el-tr:hover { background: var(--bg-hover); }
        .el-td { padding: 10px 14px; font-size: 13px; color: var(--text-secondary); display: flex; align-items: center; flex-shrink: 0; overflow: hidden; }
        .el-td:not(:last-child) { border-right: 1px solid var(--border); }
        .el-td-email { font-size: 12px; color: var(--text-muted); }
        .el-td-muted { color: var(--text-muted); font-size: 11px; }
        .el-display-name { font-weight: 500; color: var(--text-primary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .el-emp-badge { font-size: 10px; padding: 2px 8px; border-radius: var(--radius-sm); background: rgba(112, 204, 152, 0.12); color: var(--green-bright); border: 1px solid rgba(112, 204, 152, 0.28); font-weight: 600; white-space: nowrap; }
        .el-table-empty { padding: 32px 24px; text-align: center; font-size: 13px; color: var(--text-muted); }

        /* ── Load more ── */
        .el-load-more-wrap { display: flex; align-items: center; gap: 14px; padding: 12px 16px; border-top: 1px solid var(--border); background: rgba(255, 255, 255, 0.02); flex-shrink: 0; }
        .el-load-more-btn { display: flex; align-items: center; gap: 8px; font-family: var(--font-body); font-size: 13px; font-weight: 600; background: rgba(255, 255, 255, 0.03); color: var(--text-secondary); border: 1px solid var(--border-light); border-radius: var(--radius-md); padding: 7px 16px; cursor: pointer; transition: color 0.15s, border-color 0.15s, background 0.15s; }
        .el-load-more-btn:hover:not(:disabled) { color: var(--green); border-color: var(--green); }
        .el-load-more-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .el-load-more-hint { font-size: 11px; color: var(--text-muted); }
      `}</style>
    </div>
  );
}
